import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import { BsClockHistory } from 'react-icons/bs';
import { StaticImage } from 'gatsby-plugin-image';
import { FaLongArrowAltRight } from 'react-icons/fa';
import { useQueryParam } from 'use-query-params';
import { SummaryContext } from '@/features/details/summary/context';
import { MetricKind } from '@/webapi/models';
import {
  ExpVersion,
  SummaryResponse,
  VariantSummary,
} from '@/webapi/use-analytics-api';
import {
  Experience,
  ExperienceStatus,
  ExperienceVariant,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { minSessionsSampleSize } from '@/features/details/sample-size/index';
import { getChanceToWin, getUplift } from '@/features/details/summary/util';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { ConfirmModal } from '@/components/confirm-modal';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { ExperienceSpinner } from '@/components/experience-spinner';
import { maybe } from '@/features/details/utils';
import { CONTROL, DELETED } from '@/features/details/shared';
import { breakpoints } from '@/components/responsive';

function getWinnerName(
  variants: ExperienceVariant[],
  winner: { chance: number; uplift: number; id: string },
) {
  return variants?.find((v) => v.id === winner?.id)?.name || `New Variation`;
}

export const StatisticalSignificance: FC<{ experience; setExperience }> = ({
  experience,
  setExperience,
}) => {
  const {
    variants: _variants,
    data: _data,
    primaryGoal,
    showSummary,
    hasOneVariant,
  } = useContext(SummaryContext);
  const { version, startDate, endDate } = useContext(DatePickerContext);

  let variants: ExperienceVariant[] = _variants && clone(_variants);
  const data: SummaryResponse = _data && clone(_data);

  const winner = findWinner(variants, data, primaryGoal);

  const winnerName = getWinnerName(variants, winner);
  const hasControl = !!data?.controlVariationId;
  if (winner) {
    // if we have a winner, ignore other variants when calculating statistical significance
    variants = variants?.filter(
      (v) =>
        v.id === winner.id ||
        v.name === CONTROL ||
        v.id === data?.controlVariationId,
    );
    data.variants = data.variants.filter((v) =>
      variants.map((v) => v.id).includes(v.variantId),
    );
  }
  const daysRunning = countRunningDays(version, endDate, startDate);

  const { onYes, loading } = getHandleOnYes(experience, setExperience, winner);
  const { notSignificantStatistically, statisticallySignificant, daysLeft } =
    isStatisticallySignificantResult(
      variants,
      data,
      primaryGoal,
      winner,
      daysRunning,
      data?.controlVariationId,
    );
  const isSuccess = !!winner && statisticallySignificant;
  const { props, hide, show, fromRef } = getSharedElemntProps();

  if (shouldHide(hasOneVariant, version) || !showSummary) {
    return null;
  }
  if (
    (notSignificantStatistically || !hasControl) &&
    experience.status === ExperienceStatus.PUBLISHED
  ) {
    const showEstimate = Math.round(daysLeft) > 0 && Math.round(daysLeft) <= 10;
    return (
      <Container>
        <BsClockHistory />
        <span>
          There are still no statistically significant results. We recommend
          waiting a few more days
          {showEstimate
            ? `. Time to Significance ~ ${Math.round(daysLeft)} days`
            : ``}
        </span>
      </Container>
    );
  }
  return (
    isSuccess && (
      <>
        <SharedElementOverlay {...props}>
          <>
            {loading && (
              <div
                style={{
                  display: `flex`,
                  justifyContent: `center`,
                  alignItems: `center`,
                  height: `100%`,
                  width: `100%`,
                }}
              >
                <ExperienceSpinner size={3} />
              </div>
            )}
            {!loading && (
              <ConfirmModal
                neutral
                onConfirm={onYes}
                onDiscard={hide}
                title="Are you sure?"
                description={
                  <span style={{ lineHeight: `1.5` }}>
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    Click "Yes" to move this experience to 100%. <br />
                    {` `}
                    <b>A new test version will be created.</b>
                  </span>
                }
              />
            )}
          </>
        </SharedElementOverlay>

        <Container success>
          <StaticImage
            placeholder="none"
            loading="eager"
            width={17}
            formats={[`png`, `webp`]}
            src="./winner.svg"
            alt="success_star"
          />
          <span>{winnerName} is currently winning.</span>
          <button type="button" ref={fromRef} onClick={show}>
            Move to 100% <FaLongArrowAltRight />
            {` `}
          </button>
        </Container>
      </>
    )
  );
};

function getControlMetric(
  data: SummaryResponse,
  metric: MetricKind,
  control?: string,
) {
  const controlMetric = data?.variants
    ?.find((v) => v.variantId === control)
    ?.metrics?.find((m) => m?.name === metric)?.value;

  return { controlMetric };
}

function clone(experience: any) {
  return JSON.parse(JSON.stringify(experience));
}

function shouldHide(hasOneVariant: boolean, version: ExpVersion) {
  return hasOneVariant || !version?.isCurrent;
}

function getSharedElemntProps() {
  const { props, hide, show, fromRef } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(19, 42),
  );
  return { props, hide, show, fromRef };
}

function findConversionGoalMetric(primaryGoal: MetricKind) {
  const metric = MetricKind.CONVERSION_RATE;
  if (MetricKind.ADD_TO_CART_RATE === primaryGoal) {
    return MetricKind.ADD_TO_CART_RATE;
  }
  if ([MetricKind.CLICKS, MetricKind.CTR].includes(primaryGoal)) {
    return MetricKind.CTR;
  }
  if (primaryGoal === MetricKind.SIGNUPS) {
    return MetricKind.SIGNUPS_RATE;
  }
  if (primaryGoal === MetricKind.SUBSCRIPTIONS) {
    return MetricKind.SUBSCRIPTION_RATE;
  }
  return metric;
}

export function findWinner(
  variants: Array<ExperienceVariant>,
  data: SummaryResponse,
  primaryGoal: MetricKind,
) {
  return maybe(
    () =>
      variants
        .filter(
          (v) =>
            v.name !== CONTROL &&
            v.name !== DELETED &&
            v.id !== data.controlVariationId,
        )
        .map((v) => ({
          id: v.id,
          chance: getChanceToWin(data, v, primaryGoal),
          uplift: getUplift(data, primaryGoal, variants, v.id),
        }))
        .filter(({ chance, uplift }) => chance >= 80 && uplift > 0)
        .sort((a, b) => b.uplift - a.uplift)[0],
  );
}

function getMultivariantGaussMaxSampleSize(
  data: SummaryResponse,
  variants: Array<ExperienceVariant>,
) {
  return maybe(() =>
    data.variants
      ?.filter(isNonDeleted(variants))
      ?.map((v) => parseInt(v.revenueGauss?.minSampleSize, 10))
      ?.filter((x) => x > 0)
      ?.reduce((p, c) => (p > c ? p : c)),
  );
}

function calcSignificance(minSample: number, variationSessions: number) {
  const notSignificantStatistically =
    variationSessions < 1000 ||
    (!Number.isNaN(minSample) && variationSessions < minSample);

  const statisticallySignificant =
    !Number.isNaN(minSample) &&
    variationSessions > 1000 &&
    variationSessions > minSample;
  return { notSignificantStatistically, statisticallySignificant };
}

const Container = styled.div`
  min-height: 4.5rem;
  padding: 1rem 2rem;
  border-radius: 1.5rem;
  background-color: ${(p: { success?: boolean }) =>
    p?.success ? `#1ed58b` : `#eceef0`};
  color: ${(p: { success?: boolean }) => (p?.success ? `white` : `#656e72`)};
  font-family: Inter, serif;
  font-size: 1.4rem;
  margin-bottom: 2rem;

  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-gap: 2rem;
  gap: 2rem;
  justify-content: center;
  align-items: center;

  button {
    cursor: pointer;
    outline: none;
    appearance: none;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-family: 'JetBrains Mono', serif;
    font-size: 1.1rem;
    font-weight: bold;

    padding: 0.5rem 1.5rem;
    border-radius: 2rem;
    background-color: white;
    box-shadow: 0 1px 1px 0 rgba(112, 85, 237, 0.18),
      0 2px 5px 0 rgba(255, 176, 207, 0.7);

    svg {
      margin-left: 1rem;
    }

    :hover {
      opacity: 0.9;
    }

    :active {
      opacity: 0.8;
    }

    transition: opacity 0.2s linear;

    ${breakpoints.down(`md`)} {
      display: none;
    }
  }

  ${breakpoints.down(`md`)} {
    width: 90%;
    margin: 0 auto 3rem auto;
    font-size: 1.2rem;
  }
`;

function getHandleOnYes(
  experience,
  setExperience,
  winner: { chance: number; id: string },
) {
  const { upsertExperience, publishExperience, loading } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  return {
    onYes: async () => {
      if (!winner?.id) {
        return;
      }
      const clonedExperience = selectWinner(experience, winner);
      await upsertExperience(clonedExperience);
      await publishExperience(clonedExperience.id, true);
      setExperience(clonedExperience);
      window.location.reload();
    },
    loading,
  };
}

export function findMinSessionsCount(
  data: SummaryResponse,
  variants: Array<ExperienceVariant>,
  control: string,
) {
  return maybe(() =>
    data.variants
      ?.filter(isNonControl(control))
      ?.filter(isNonDeleted(variants))
      ?.map(
        (v) => v.metrics?.find((m) => m.name === MetricKind.SESSIONS)?.value,
      )
      ?.reduce((p, c) => (p < c ? p : c)),
  );
}

export function minOrdersSampleSize(
  data: SummaryResponse,
  variants: Array<ExperienceVariant>,
) {
  if (data?.revenueGauss?.minSampleSize?.length > 0) {
    return parseInt(data?.revenueGauss?.minSampleSize, 10);
  }
  return getMultivariantGaussMaxSampleSize(data, variants);
}

export function getMinSessionsSampleSize(
  variants: Array<ExperienceVariant>,
  data: SummaryResponse,
  metric: MetricKind,
  control: string,
) {
  const { controlMetric } = getControlMetric(data, metric, control);
  let maxMinSampleSize = 100;
  if (data?.variants) {
    maxMinSampleSize = maybe(
      () =>
        data?.variants
          ?.filter(isNonControl(control))
          ?.filter(isNonDeleted(variants))
          .flatMap((x) => x?.metrics?.find((m) => m?.name === metric)?.value)
          ?.map((variationMetric) =>
            minSessionsSampleSize(
              controlMetric,
              Math.abs(controlMetric - variationMetric),
            ),
          )
          ?.reduce((p, c) => (p < c ? c : p)),
      100,
    );
  }

  return maxMinSampleSize;
}

export function selectWinner(
  experience: Experience,
  winner: { chance: number; id: string },
) {
  const clonedExperience = clone(experience);

  clonedExperience?.variants?.forEach((v, i) => {
    if (v.id === winner?.id) {
      clonedExperience.variants[i].chance = 100;
      clonedExperience.variants[i].publishedChance = 100;
    } else {
      clonedExperience.variants[i].chance = 0;
      clonedExperience.variants[i].publishedChance = 0;
    }
  });
  clonedExperience.metricsStartAt = new Date().toISOString();

  return clonedExperience;
}

const isNonDeleted =
  (variants: Array<ExperienceVariant>) => (v: VariantSummary) =>
    !variants
      .filter((v) => v.name === DELETED)
      .map((v) => v.id)
      .includes(v.variantId);

function isNonControl(control: string) {
  return (v) => v.variantId !== control;
}

function isPsv(primaryGoal: MetricKind) {
  return [MetricKind.PROFIT_PER_SESSION, MetricKind.PER_SESSION_VALUE].includes(
    primaryGoal,
  );
}

function isAov(primaryGoal: MetricKind) {
  return [
    MetricKind.AVG_ORDER_VALUE,
    MetricKind.REVENUE,
    MetricKind.PROFIT,
  ].includes(primaryGoal);
}

function isStatisticallySignificantResult(
  variants: ExperienceVariant[],
  data: SummaryResponse,
  primaryGoal: MetricKind,
  winner: {
    chance: number;
    uplift: number;
    id: string;
  },
  daysRunning: number,
  controlVariantId: string, // in case there is no control - take controlVariantId...
) {
  const metric = findConversionGoalMetric(primaryGoal);
  const requiredSessions = getMinSessionsSampleSize(
    variants,
    data,
    metric,
    controlVariantId,
  );

  const variationSessions = findMinSessionsCount(
    data,
    variants,
    controlVariantId,
  );

  let daysLeft = maybe(() => {
    if (daysRunning <= 0) {
      return -1;
    }
    const sessionsLeft = requiredSessions - variationSessions;

    return (sessionsLeft * daysRunning) / variationSessions;
  }, -1);

  let { notSignificantStatistically, statisticallySignificant } =
    calcSignificance(requiredSessions, variationSessions);

  if (isPsv(primaryGoal) && statisticallySignificant) {
    return { notSignificantStatistically, statisticallySignificant, daysLeft };
  }

  const { gaussSignificant, daysLeft: daysLeftGauss } =
    isGaussStatisticallySignificant(data, variants, winner, daysRunning);
  if (typeof gaussSignificant === `boolean`) {
    if (isAov(primaryGoal) || daysLeftGauss <= daysLeft) {
      daysLeft = daysLeftGauss;
    }
    if (gaussSignificant) {
      statisticallySignificant = true;
      notSignificantStatistically = false;
    } else if (!isPsv(primaryGoal)) {
      statisticallySignificant = false;
      notSignificantStatistically = true;
    }
  }
  return { notSignificantStatistically, statisticallySignificant, daysLeft };
}

function isGaussStatisticallySignificant(
  data: SummaryResponse,
  variants: ExperienceVariant[],
  winner: { id: string },
  daysRunning: number,
) {
  const minOrders = minOrdersSampleSize(data, variants);
  const winnerTransactions = data?.variants
    ?.find((x) => x.variantId === winner?.id || variants?.[0]?.id)
    ?.metrics?.find((m) => m.name === MetricKind.TRANSACTIONS)?.value;
  if (typeof minOrders === `number` && typeof winnerTransactions === `number`) {
    const daysLeft = maybe(() => {
      if (daysRunning <= 0) {
        return -1;
      }
      const ordersLeft = minOrders - winnerTransactions;
      return (ordersLeft * daysRunning) / winnerTransactions;
    }, -1);
    return { gaussSignificant: minOrders < winnerTransactions, daysLeft };
  }
  return { gaussSignificant: null, daysLeft: -1 };
}

function countRunningDays(version: ExpVersion, endDate: Date, startDate: Date) {
  const [audience] = useQueryParam(`aud`);
  try {
    let daysDifference = -1;
    if (version?.isCurrent && !audience) {
      const timeDifference = Math.abs(endDate.valueOf() - startDate.valueOf());
      daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
      return daysDifference <= 1 ? -1 : daysDifference;
    }
    return -1;
  } catch (e) {
    return -1;
  }
}
